import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";

function initials(name: string): string {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Administrator",
  officer: "Procurement Officer",
  scm_manager: "SCM Manager",
  cfo_hod: "CFO / HOD",
};

const navItems = [
  { path: "/", label: "Dashboard", icon: "ri-dashboard-3-line" },
  { path: "/contracts", label: "Contracts", icon: "ri-file-list-3-line" },
  { path: "/supplier-reviews", label: "Supplier Reviews", icon: "ri-star-line" },
  { path: "/sla-matrix", label: "SLA Matrix", icon: "ri-survey-line" },
  { path: "/alerts", label: "Alert Settings", icon: "ri-notification-3-line" },
  { path: "/departments", label: "Departments & Users", icon: "ri-building-2-line" },
  { path: "/reports", label: "Reports", icon: "ri-bar-chart-2-line" },
  { path: "/billing", label: "Billing", icon: "ri-bank-card-line" },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside className="fixed left-0 top-0 h-screen w-60 bg-sidebar flex flex-col z-30">
      {/* Logo */}
      <div className="h-16 flex items-center px-5 border-b border-white/15 shrink-0">
        <div className="flex items-center gap-2.5">
          <img src="/logo-mark.svg" alt="SupplyGuard" className="w-8 h-8 rounded-md" />
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold leading-tight">SupplyGuard</p>
            <p className="text-white/75 text-[10px] leading-tight truncate">{user?.institution?.name ?? "Loading…"}</p>
          </div>
        </div>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 overflow-y-auto sidebar-scroll py-3 px-3">
        {navItems.map((item) => {
          const isActive = item.path === "/"
            ? location.pathname === "/"
            : location.pathname.startsWith(item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium mb-1 transition-colors whitespace-nowrap ${
                isActive
                  ? "bg-white/20 text-white"
                  : "text-white/75 hover:text-white hover:bg-white/10"
              }`}
            >
              <i className={`${item.icon} text-base`}></i>
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-white/15 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center text-white text-[11px] font-semibold shrink-0">
            {user ? initials(user.name) : "?"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-medium truncate">{user?.name ?? "—"}</p>
            <p className="text-white/75 text-[10px] truncate">{user ? (ROLE_LABELS[user.role] ?? user.role) : ""}</p>
          </div>
          <button onClick={handleLogout} className="w-7 h-7 rounded-md flex items-center justify-center text-white/75 hover:text-white hover:bg-white/10 transition-colors cursor-pointer">
            <i className="ri-logout-box-r-line text-sm"></i>
          </button>
        </div>
      </div>
    </aside>
  );
}